import { IBuildingBlockFeatureDescription } from '@/types/contentful';
import GlImage from '../Image';
import { getImageData } from '~/utils/contentful';
import { Asset } from 'contentful';
import { Box } from '../Box';
import RichText from '../RichText';
import { BLOCKS } from '@contentful/rich-text-types';
import cx from 'classnames';
import { StandardWidget } from '../CtaWidget/StandardWidget';
import { TextLink } from '../AtomicComponents/TextLink';

interface FeatureDescriptionsProps {
  cardsColors: string | undefined;
  featureDescriptions: IBuildingBlockFeatureDescription[];
}

export function FeatureDescriptions({
  cardsColors,
  featureDescriptions
}: FeatureDescriptionsProps) {
  const hasTwoCards = featureDescriptions?.length === 2;
  const hasThreeCards = featureDescriptions?.length === 3;
  const hasFourCards = featureDescriptions?.length === 4;

  return (
    <div className="relative w-full overflow-hidden">
      <div
        className={cx(
          'flex w-full max-w-[100vw] items-stretch justify-start gap-6 overflow-x-auto overflow-y-hidden scroll-smooth transition-all',
          hasTwoCards && 'tablet:justify-center',
          hasThreeCards && 'desktop:justify-center',
          hasFourCards && 'max:justify-center'
        )}
        style={{ scrollBehavior: 'smooth' }}
      >
        <div className="flex gap-4 px-4 tablet:gap-6 tablet:px-6">
          {(featureDescriptions || []).map(({ sys, fields }: any) => (
            <div
              key={sys.id}
              className={cx(
                'flex h-full w-[290px] flex-shrink-0 flex-col items-center',
                hasTwoCards && 'w-[290px] tablet:w-[300px] desktop:w-[410px]',
                hasThreeCards && 'w-[290px] tablet:w-[300px] desktop:w-[330px]',
                hasFourCards && 'w-[290px] tablet:w-[300px] desktop:w-[300px]'
              )}
            >
              <div className="relative h-[224px] w-full rounded-2xl">
                <GlImage
                  {...getImageData(fields?.cardImage as Asset)}
                  className="h-56 w-full rounded-t-2xl"
                  loading="eager"
                  heroImage={false}
                  objectFit="cover"
                />
              </div>
              <div
                className="z-0 flex h-full w-full flex-col justify-between rounded-b-2xl bg-warm-neutral-100 px-6 pb-6"
                style={{
                  backgroundColor: cardsColors ? cardsColors : undefined
                }}
              >
                <div className="flex-grow">
                  <h3 className="!typography-display-4 mb-4 mt-6 text-left">
                    {fields?.cardHeader}
                  </h3>
                  <Box className="flex items-start text-left">
                    <RichText
                      data={fields.descriptionBullets}
                      overrideOptions={{
                        [BLOCKS.UL_LIST]: (_node, children) => (
                          <ul
                            className={cx(
                              'styled-feature-description-list text-base',
                              !fields?.buttonLink && '[&>li:last-child]:mb-0'
                            )}
                          >
                            {children}
                          </ul>
                        ),
                        [BLOCKS.PARAGRAPH]: (_node, children) => (
                          <p className="!typography-body-2 font-[350] leading-5">
                            {children}
                          </p>
                        )
                      }}
                    />
                  </Box>
                </div>
                <div className="w-full flex-shrink-0">
                  {fields?.buttonLink && fields?.buttonText ? (
                    <div className="w-full">
                      <StandardWidget
                        buttonText={fields.buttonText}
                        buttonVariant="primary"
                        fullWidth
                        onClick={() =>
                          fields?.buttonLink &&
                          (window.location.href = fields.buttonLink)
                        }
                      />
                    </div>
                  ) : fields?.optionalTextLink ? (
                    <div className="mt-2">
                      <TextLink
                        href={fields?.optionalTextLink as string}
                        showArrow
                      >
                        Learn more
                      </TextLink>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
